import { esc, openModal } from '../ui/modal';
import { t } from '../ui/lang';
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, loadPageSize, savePageSize, pageSizeOptionsHtml, dayBoundary, fmtTime, pill, } from './clicker-table-shared';
import { formatMinorsGrouped } from './clicker-amount-fields';
const PAGE_SIZE_KEY = 'mf.runLogPageSize';
const COLS = 5;
/** Start ▷ / Stop ▢ pill, coloured like the card controls (green start, red stop). */
function actionPill(action) {
    if (action === 'close_shift')
        return pill(t('shifts.close_shift'), 'muted');
    return action === 'start' ? pill(t('clickertab.hist_action_start'), 'ok') : pill(t('clickertab.hist_action_stop'), 'fail');
}
/**
 * Who triggered it. A panel action names the user (login + role); the phone's own clicker
 * card and the panel's automatic actions are labelled as such, with the reason the server
 * recorded ("phone offline ~11 min", "runner restart resume") — so an auto-stop is never
 * misread as somebody's decision.
 */
function actorCell(r) {
    const reason = r.reason ? `<div class="settings-hint">${esc(r.reason)}</div>` : '';
    if (r.source === 'panel' && r.actor_login) {
        return `<strong>${esc(r.actor_login)}</strong>${r.actor_role ? ` <span class="settings-hint">${esc(r.actor_role)}</span>` : ''}${reason}`;
    }
    const label = r.source === 'device' ? t('clickertab.hist_actor_phone') : t('clickertab.hist_actor_panel');
    return `<span class="settings-hint">${esc(label)}</span>${reason}`;
}
/**
 * One min/max pair as "10 000 00 – 90 000 00", grouped exactly like the form fields it
 * was typed into (see clicker-amount-fields.ts — every bound is in MINORS). A missing
 * side degrades to "from …" / "up to …"; 0 is "no bound", not a value, so a pair with
 * neither bound renders '' and the caller drops the line.
 */
function amountRange(min, max) {
    const lo = min ? formatMinorsGrouped(min) : '';
    const hi = max ? formatMinorsGrouped(max) : '';
    if (lo && hi)
        return t('clickertab.hist_range', { min: lo, max: hi });
    if (lo)
        return t('clickertab.hist_range_from', { min: lo });
    if (hi)
        return t('clickertab.hist_range_to', { max: hi });
    return '';
}
/**
 * What the run was launched with: app + task type, which is what identifies a run at a
 * glance, plus the two amount settings that decide what it actually did — the payout
 * bounds (MIN/MAX_INITIAL_AMOUNT: which tasks it accepted) and the deposit band
 * (DEPOSIT_MIN/MAX_BALANCE: when the `auto` task switched sides). Those are the settings
 * a reader compares between two runs when the device suddenly behaves differently, and
 * unlike the rest they are per-run overrides, not farm-wide constants. A bound that was
 * not set is left out rather than printed as "—".
 *
 * The row carries the rest of the resolved params (bucket, TG chats, api host, script,
 * pin/password presence) and they stay in `clicker_run_events`, but printing them turned
 * the cell into a wall of small print. The Telegram handle is the one exception: it
 * answers "who does Clicker Bot ping for this run" (on a cancel or decline), which is not
 * derivable from the actor column once an automatic resume takes over.
 */
function paramsCell(p) {
    if (!p)
        return '<span class="settings-hint">—</span>';
    const amounts = [
        [t('clickertab.hist_payouts'), amountRange(p.min_initial_amount, p.max_initial_amount)],
        [t('clickertab.hist_deposits'), amountRange(p.deposit_min_balance, p.deposit_max_balance)],
    ];
    const amountLines = amounts
        .filter(([, range]) => range)
        .map(([label, range]) => `<div class="settings-hint">${label}: ${range}</div>`)
        .join('');
    const tag = p.operator_tg ? `<div class="settings-hint">${t('clickertab.hist_tags_handle', { tg: esc(p.operator_tg) })}</div>` : '';
    return `<strong>${esc(p.app_name || t('clickertab.hist_app_auto'))}</strong> · ${esc(p.task_type)}${amountLines}${tag}`;
}
/** Outcome: the action went through, or the reason it didn't. */
function resultCell(r) {
    if (r.ok)
        return pill(t('clickertab.hist_result_ok'), 'ok');
    return `${pill(t('clickertab.hist_result_failed'), 'fail')}${r.error ? ` <span class="settings-hint">${esc(r.error)}</span>` : ''}`;
}
/**
 * Per-device clicker run history in a big popup (same chrome as the operations registry):
 * who started/stopped this device's clicker, when, and with which launch params — panel
 * users, the phone's own card and the panel's automatic actions alike. Admin-only, matching
 * the endpoint level; call sites gate the button with can('device.clickerHistory').
 */
export function openClickerHistoryWindow(farmId, udid, deviceName) {
    const title = deviceName ? `${deviceName}` : udid;
    const initialPageSize = loadPageSize(PAGE_SIZE_KEY);
    const modal = openModal(`
        <h2 class="modal__title">${t('clickertab.hist_title')} — ${esc(title)}</h2>
        <div class="clicker-ops-toolbar">
            <label class="ops-filter">${t('clickertab.from')} <input type="date" class="settings-input hist-from" /></label>
            <label class="ops-filter">${t('clickertab.to')} <input type="date" class="settings-input hist-to" /></label>
            <label class="ops-filter">${t('clickertab.action')}
                <select class="settings-input hist-action">
                    <option value="">${t('clickertab.all')}</option>
                    <option value="start">${t('clickertab.hist_action_start')}</option>
                    <option value="stop">${t('clickertab.hist_action_stop')}</option>
                </select>
            </label>
            <span class="clicker-ops-spacer"></span>
            <label class="ops-filter">${t('clickertab.per_page')}
                <select class="settings-input hist-page-size">
                    ${pageSizeOptionsHtml(initialPageSize)}
                </select>
            </label>
            <button type="button" class="tbl-btn hist-refresh" title="${t('common.refresh')}">${t('common.refresh')}</button>
            <button type="button" class="tbl-btn hist-prev" disabled>${t('clickertab.prev')}</button>
            <span class="clicker-ops-page">—</span>
            <button type="button" class="tbl-btn hist-next" disabled>${t('clickertab.next')}</button>
        </div>
        <div class="clicker-ops-table-wrap">
            <table class="access-matrix clicker-ops-table clicker-hist-table">
                <thead><tr>
                    <th>${t('clickertab.time')}</th><th>${t('clickertab.action')}</th><th>${t('clickertab.by')}</th><th>${t('clickertab.launch_params')}</th><th>${t('clickertab.result')}</th>
                </tr></thead>
                <tbody class="clicker-hist-body"></tbody>
            </table>
        </div>
    `);
    modal.classList.add('modal--clicker-log', 'modal--clicker-ops');
    const fromEl = modal.querySelector('.hist-from');
    const toEl = modal.querySelector('.hist-to');
    const actionEl = modal.querySelector('.hist-action');
    const refreshBtn = modal.querySelector('.hist-refresh');
    const prevBtn = modal.querySelector('.hist-prev');
    const nextBtn = modal.querySelector('.hist-next');
    const pageEl = modal.querySelector('.clicker-ops-page');
    const pageSizeEl = modal.querySelector('.hist-page-size');
    const bodyEl = modal.querySelector('.clicker-hist-body');
    let page = 1;
    let total = 0;
    let pageSize = initialPageSize;
    const setBody = (html) => { bodyEl.innerHTML = html; };
    // Full-width status row (loading / empty / error). The error variant needs the explicit
    // display, since .modal__error is hidden by default.
    const message = (text, error = false) => `<tr><td colspan="${COLS}" class="${error ? 'modal__error' : 'settings-hint'}" style="text-align:center${error ? ';display:table-cell' : ''}">${esc(text)}</td></tr>`;
    const render = (data) => {
        total = data.total;
        if (data.rows.length === 0) {
            setBody(message(t('clickertab.hist_empty')));
        }
        else {
            setBody(data.rows.map((r) => `
                <tr>
                    <td>${fmtTime(r.created_at)}</td>
                    <td>${actionPill(r.action)}</td>
                    <td>${actorCell(r)}</td>
                    <td class="clicker-hist-params">${paramsCell(r.params)}</td>
                    <td>${resultCell(r)}</td>
                </tr>
            `).join(''));
        }
        const pages = Math.max(1, Math.ceil(total / pageSize));
        pageEl.textContent = t(total === 1 ? 'clickertab.hist_pager_one' : 'clickertab.hist_pager_many', { page: data.page, pages, total });
        prevBtn.disabled = data.page <= 1;
        nextBtn.disabled = data.page >= pages;
    };
    // Filters change faster than the requests come back, so every load carries a sequence
    // number and only the newest one is allowed to touch the table (see the ops registry).
    let reqSeq = 0;
    const load = async () => {
        const seq = ++reqSeq;
        setBody(message(t('common.loading')));
        const params = new URLSearchParams();
        const fromIso = fromEl.value ? dayBoundary(fromEl.value) : null;
        const toIso = toEl.value ? dayBoundary(toEl.value, 1) : null; // inclusive of the picked day
        if (fromIso)
            params.set('from', fromIso);
        if (toIso)
            params.set('to', toIso);
        if (actionEl.value)
            params.set('action', actionEl.value);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        try {
            const res = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}/run-log?${params.toString()}`);
            if (seq !== reqSeq)
                return;
            if (!res.ok) {
                setBody(message(t('clickertab.hist_failed_to_load'), true));
                prevBtn.disabled = true;
                nextBtn.disabled = true;
                return;
            }
            const data = await res.json();
            if (seq !== reqSeq)
                return;
            render(data);
        }
        catch {
            if (seq !== reqSeq)
                return;
            setBody(message(t('clickertab.network_error_dot'), true));
        }
    };
    // Any filter change resets to the first page; the pager steps within the result set.
    const reload = () => { page = 1; void load(); };
    fromEl.addEventListener('change', reload);
    toEl.addEventListener('change', reload);
    actionEl.addEventListener('change', reload);
    refreshBtn.addEventListener('click', () => { void load(); }); // current page + filters
    prevBtn.addEventListener('click', () => { if (page > 1) {
        page--;
        void load();
    } });
    nextBtn.addEventListener('click', () => {
        if (page < Math.ceil(total / pageSize)) {
            page++;
            void load();
        }
    });
    pageSizeEl.addEventListener('change', () => {
        const n = parseInt(pageSizeEl.value, 10);
        pageSize = PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
        savePageSize(PAGE_SIZE_KEY, pageSize);
        page = 1;
        void load();
    });
    void load();
}
